/**
 * Lo mínimo que hace falta para ordenar la grilla.
 *
 * Es estructural y no `ProductoPos` porque la pantalla de configuración ordena
 * su propia vista previa con ESTE comparador y su DTO no es el del POS: una
 * vista previa con su propia cuenta es una que un día muestra un orden distinto
 * del real, que es peor que no tenerla.
 */
export interface OrdenableEnLaGrilla {
  id: number
  nombre: string
  categoriaNombre: string
  ordenPos: number
  vecesVendidas?: number
}

/**
 * Con qué criterio se acomoda la grilla del mostrador.
 *
 * Los códigos son los de `OrdenDeLaGrilla` del backend, y los sirven los DOS
 * hosts en /pos/opciones.
 */
export type CriterioOrden =
  | 'manual'
  | 'alfabetico'
  | 'alfabetico_desc'
  | 'categoria'
  | 'mas_vendidos'

/** Lo que dice cada criterio, para las pantallas que lo muestran. */
export const ETIQUETAS_ORDEN: Record<CriterioOrden, string> = {
  manual: 'El orden que acomoda el local',
  mas_vendidos: 'Los más vendidos primero',
  alfabetico: 'Alfabético (A → Z)',
  alfabetico_desc: 'Alfabético (Z → A)',
  categoria: 'Por categoría, y adentro alfabético',
}

const CRITERIOS = Object.keys(ETIQUETAS_ORDEN) as CriterioOrden[]

/**
 * Normaliza lo que mandó el host.
 *
 * Un criterio que este bundle no conoce cae en `manual`, que es el orden que la
 * grilla tuvo siempre: el ejecutable de la caja se actualiza por OTA y la nube
 * puede ir adelante, así que lo peor posible sería dejar la grilla sin ordenar
 * porque llegó una palabra nueva.
 */
export function criterioDeOrden(valor: string | null | undefined): CriterioOrden {
  const v = (valor ?? '').trim().toLowerCase() as CriterioOrden

  return CRITERIOS.includes(v) ? v : 'manual'
}

/**
 * Los productos de la grilla, ordenados con el criterio que eligió el local.
 *
 * Ordena la PANTALLA y no el servidor, y es la decisión del módulo: el SPA es
 * uno solo y los dos hosts tendrían que escribir el mismo criterio cada uno en
 * su consulta, que es exactamente cómo un día la caja del mostrador termina
 * ordenando distinto que la caja web. Acá hay un comparador y tiene tests.
 *
 * Devuelve una copia: la lista que llega es la de la consulta, y react-query la
 * cachea —ordenarla en el lugar la dejaría mutada para todos los que la leen—.
 */
export function ordenarProductos<T extends OrdenableEnLaGrilla>(
  productos: T[],
  criterio: CriterioOrden,
): T[] {
  return [...productos].sort(comparador)

  function comparador(a: T, b: T): number {
    switch (criterio) {
      case 'alfabetico':
        return porNombre(a, b)

      case 'alfabetico_desc':
        return -porNombre(a, b)

      case 'categoria':
        return a.categoriaNombre.localeCompare(b.categoriaNombre, 'es') || porNombre(a, b)

      case 'mas_vendidos':
        // Los que más salieron arriba, y a igual cantidad por nombre. El que
        // nunca se vendió viene en cero, así que la cola de la grilla queda
        // alfabética en lugar de en un orden que nadie puede explicar.
        return (b.vecesVendidas ?? 0) - (a.vecesVendidas ?? 0) || porNombre(a, b)

      case 'manual':
      default:
        return a.ordenPos - b.ordenPos || porNombre(a, b)
    }
  }
}

/**
 * El desempate de TODOS los criterios, y por eso termina siempre en el id.
 *
 * Sin un orden total, dos productos que empatan pueden quedar en un orden
 * distinto en cada render —el sort del navegador es estable, pero la lista que
 * llega del host no tiene por qué llegar siempre igual—, y en el POS eso
 * significa que el botón se le mueve al cajero abajo del dedo.
 */
function porNombre(a: OrdenableEnLaGrilla, b: OrdenableEnLaGrilla): number {
  return a.nombre.localeCompare(b.nombre, 'es') || a.id - b.id
}
